import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow as MuiTableRow,
  TextField,
  Typography,
} from "@mui/material";
import { STATUS_BADGE } from "../data/mockData";
import type { SortKey, TableRow } from "../types/timeEfficiency.types";
import { fmtDias } from "../utils/format";
import { TE_CARD_SX, TE_COLORS, TE_PANEL_TITLE_SX } from "./theme";

function buildColumns(
  tempoTotalAvailable: boolean,
  reaberturasAvailable: boolean,
  /** Nos drill-downs "Etapa mais demorada"/"Etapa com mais projetos
   * parados agora", "Entrou em"/"Tempo total" passam a significar a
   * PRIMEIRA entrada na etapa filtrada e o tempo TOTAL acumulado nela
   * (reaberturas incluídas) -- renomeia as colunas pra deixar isso
   * explícito, já que o valor exibido muda de significado. */
  stageFilterActive: boolean
): Array<{
  key: SortKey;
  label: string;
  numeric?: boolean;
  /** Api ainda não calcula esse valor de verdade -- não faz sentido ordenar. */
  notAvailable?: boolean;
}> {
  return [
    { key: "id", label: "Projeto" },
    { key: "projeto", label: "Nome do Evento" },
    { key: "tipoEvento", label: "Tipo de Evento" },
    { key: "numPessoas", label: "Nº Participantes", numeric: true },
    { key: "cliente", label: "Cliente" },
    { key: "status", label: "Status atual" },
    {
      key: "entrouEm",
      label: stageFilterActive ? "1ª entrada na etapa" : "Entrou no status em",
    },
    { key: "tempoNoStatus", label: "Tempo no status", numeric: true },
    {
      key: "tempoTotal",
      label: stageFilterActive ? "Tempo total na etapa" : "Tempo total",
      numeric: true,
      notAvailable: !tempoTotalAvailable,
    },
    { key: "inicio", label: "Início do projeto" },
    { key: "criacao", label: "Criado em" },
    { key: "justificativa", label: "Justificativa" },
    {
      key: "reaberturas",
      label: "Reaberturas",
      numeric: true,
      notAvailable: !reaberturasAvailable,
    },
  ];
}

interface DetailTableProps {
  rows: TableRow[];
  totalCount: number;
  searchTerm: string;
  onSearch: (v: string) => void;
  sortKey: SortKey;
  sortDir: 1 | -1;
  onToggleSort: (key: SortKey) => void;
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onOpenHistory: (projectId: string, displayId: string) => void;
  onOpenProject: (projectId: string) => void;
  onExport: () => void;
  /** Modo atual já traz duração total real (drill-down "acima da média"
   * ou "com reabertura"). */
  tempoTotalAvailable?: boolean;
  /** Modo atual já traz contagem real de reaberturas (drill-down "com
   * reabertura"). */
  reaberturasAvailable?: boolean;
  /** Qual fonte alimenta `rows` agora -- só pra escolher o texto de
   * descrição certo (as 3 colunas em si já são reais nos 3 modos). */
  mode?: "default" | "aboveAverage" | "reopen" | "stageFilter";
  /** Busca em andamento (troca de filtro/modo/página) -- mostra overlay de
   * loading em vez de deixar a tabela antiga parada sem feedback. */
  loading?: boolean;
}

/** Colunas que a Api ainda não calcula de verdade (valor hoje é só um
 * placeholder, não dado real) -- mostra em vermelho pra deixar isso
 * explícito pro PO em vez de parecer um "0"/"–" normal. */
function NaoTemos() {
  return (
    <Typography
      component="span"
      sx={{ color: TE_COLORS.red, fontWeight: 700, fontSize: 11 }}
    >
      NÃO TEMOS
    </Typography>
  );
}

/** Diferente de `NaoTemos` (a Api ainda não calcula esse valor) -- aqui a
 * Api calculou, mas não existe nenhum dado de verdade (audit de troca de
 * status nem `statusChangedAt`) pra esse projeto específico, geralmente
 * projetos legados anteriores ao rastreio de status. Mostrar um número
 * nesse caso seria mostrar a idade total do projeto como se fosse o tempo
 * na etapa atual -- enganoso (bug real reportado pelo usuário). */
function SemHistorico() {
  return (
    <Typography
      component="span"
      sx={{ color: TE_COLORS.textFaint, fontStyle: "italic", fontSize: 11 }}
      title="Projeto sem histórico de status registrado -- não é possível calcular o tempo real na etapa atual."
    >
      Sem histórico
    </Typography>
  );
}

export function DetailTable({
  rows,
  totalCount,
  searchTerm,
  onSearch,
  sortKey,
  sortDir,
  onToggleSort,
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onOpenHistory,
  onOpenProject,
  onExport,
  tempoTotalAvailable = false,
  reaberturasAvailable = false,
  mode = "default",
  loading = false,
}: DetailTableProps) {
  const COLUMNS = buildColumns(
    tempoTotalAvailable,
    reaberturasAvailable,
    mode === "stageFilter"
  );
  return (
    <Box sx={TE_CARD_SX} className="table-panel">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <Box>
          <Typography sx={TE_PANEL_TITLE_SX}>
            Análise detalhada dos projetos
          </Typography>
          <Typography
            sx={{ fontSize: 11.5, color: TE_COLORS.textDim, mt: 0.25 }}
          >
            {mode === "reopen"
              ? "Projetos (concluídos e em andamento) que voltaram a um status que já tinham passado, ordenados pelo maior número de reaberturas."
              : mode === "aboveAverage"
              ? "Finalizados com tempo total acima da média do período, ordenados por maior tempo total."
              : mode === "stageFilter"
              ? 'Projetos na etapa selecionada -- "1ª entrada na etapa" e "Tempo total na etapa" somam todas as visitas a ela (reaberturas incluídas), não só a atual.'
              : "Todos os projetos do período (concluídos e em andamento), ordenados por maior tempo no status atual."}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={onExport}>
          Exportar
        </Button>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        my={1.75}
        flexWrap="wrap"
        gap={1}
      >
        <TextField
          size="small"
          placeholder="Buscar projeto ou cliente..."
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          sx={{ width: 280 }}
        />
      </Box>

      <Box sx={{ position: "relative" }}>
        {loading && rows.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(6px)",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <MuiTableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.numeric ? "right" : "left"}
                    onClick={
                      col.notAvailable ? undefined : () => onToggleSort(col.key)
                    }
                    sx={{
                      cursor: col.notAvailable ? "default" : "pointer",
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      color:
                        sortKey === col.key
                          ? TE_COLORS.text
                          : TE_COLORS.textFaint,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                    {sortKey === col.key ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </TableCell>
                ))}
                <TableCell />
              </MuiTableRow>
            </TableHead>
            <TableBody>
              {!rows.length && loading && (
                <MuiTableRow>
                  <TableCell
                    colSpan={COLUMNS.length + 1}
                    align="center"
                    sx={{ color: TE_COLORS.textFaint, py: 4 }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1.25}
                    >
                      <CircularProgress size={18} />
                      Carregando projetos...
                    </Box>
                  </TableCell>
                </MuiTableRow>
              )}
              {!rows.length && !loading && (
                <MuiTableRow>
                  <TableCell
                    colSpan={COLUMNS.length + 1}
                    align="center"
                    sx={{ color: TE_COLORS.textFaint }}
                  >
                    Nenhum projeto encontrado.
                  </TableCell>
                </MuiTableRow>
              )}
              {rows.map((row) => {
                const badge = STATUS_BADGE[row.status] || {
                  bg: "#eee",
                  fg: "#666",
                };
                return (
                  <MuiTableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.projectId ? (
                        <Typography
                          component="button"
                          onClick={() => onOpenProject(row.projectId!)}
                          sx={{
                            all: "unset",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "inherit",
                            color: TE_COLORS.blue,
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.id}
                        </Typography>
                      ) : (
                        row.id
                      )}
                    </TableCell>
                    <TableCell>{row.projeto}</TableCell>
                    <TableCell>{row.tipoEvento || "–"}</TableCell>
                    <TableCell align="right">
                      {row.numPessoas != null ? row.numPessoas : "–"}
                    </TableCell>
                    <TableCell sx={{ color: TE_COLORS.textDim }}>
                      {row.cliente}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: 1.1,
                          py: 0.3,
                          borderRadius: "14px",
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: badge.bg,
                          color: badge.fg,
                        }}
                      >
                        {row.status}
                      </Box>
                    </TableCell>
                    <TableCell>{row.entrouEm}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: TE_COLORS.red, fontWeight: 700 }}
                    >
                      {row.tempoNoStatusDisponivel === false ? (
                        <SemHistorico />
                      ) : (
                        fmtDias(row.tempoNoStatus)
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {tempoTotalAvailable ? (
                        fmtDias(row.tempoTotal)
                      ) : (
                        <NaoTemos />
                      )}
                    </TableCell>
                    <TableCell>{row.inicio}</TableCell>
                    <TableCell>{row.criacao}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 170,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.justificativa}
                    >
                      {row.justificativa}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {reaberturasAvailable ? (
                        row.projectId ? (
                          <Typography
                            component="button"
                            onClick={() =>
                              onOpenHistory(row.projectId!, row.id)
                            }
                            sx={{
                              all: "unset",
                              cursor: "pointer",
                              fontWeight: 600,
                              fontSize: "inherit",
                              color:
                                row.reaberturas > 0
                                  ? TE_COLORS.blue
                                  : "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {row.reaberturas}
                          </Typography>
                        ) : (
                          row.reaberturas
                        )
                      ) : (
                        <NaoTemos />
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={() =>
                        row.projectId && onOpenHistory(row.projectId, row.id)
                      }
                      sx={{ cursor: "pointer", color: TE_COLORS.textFaint }}
                    >
                      ⋮
                    </TableCell>
                  </MuiTableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1.75}
        flexWrap="wrap"
        gap={1}
      >
        <Typography sx={{ fontSize: 12, color: TE_COLORS.textDim }}>
          {totalCount} projeto(s)
        </Typography>
        {totalCount > 0 && (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Button
              size="small"
              disabled={currentPage <= 1}
              onClick={onPrevPage}
            >
              ‹ Anterior
            </Button>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              Página {currentPage} de {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={currentPage >= totalPages}
              onClick={onNextPage}
            >
              Próxima ›
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
